import { useCallback, useState } from 'react'
import { playBlockSound, playKillSound } from '../utils/sounds'

// ─── Action result banner ─────────────────────────────────────────────────────
function ActionResult({ result, onDismiss }) {
  if (!result) return null
  return (
    <div onClick={e => e.stopPropagation()} style={{
      marginTop: 10,
      padding: '10px 12px',
      borderRadius: 8,
      background: result.ok ? 'rgba(62,207,142,0.07)' : 'rgba(239,68,68,0.07)',
      border: `1px solid ${result.ok ? 'rgba(62,207,142,0.25)' : 'rgba(239,68,68,0.25)'}`,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 14 }}>{result.ok ? '✅' : '❌'}</span>
        <span style={{ fontSize: 12, color: result.ok ? 'var(--accent-green)' : 'var(--accent-red)', lineHeight: 1.45 }}>
          {result.ok ? result.message : result.error}
        </span>
      </div>
      <button onClick={onDismiss} style={{
        background: 'none', border: 'none', cursor: 'pointer',
        color: 'var(--text-tertiary)', fontSize: 14, flexShrink: 0, padding: 0,
        lineHeight: 1,
      }}>×</button>
    </div>
  )
}

// ─── Confirm modal ────────────────────────────────────────────────────────────
function ConfirmModal({ action, conn, onConfirm, onCancel }) {
  const isKill  = action === 'kill'
  const title   = isKill ? `Kill ${conn.appName}?` : `Block ${conn.remoteIP}?`
  const body    = isKill
    ? `This will terminate the ${conn.appName} process (PID ${conn.pid}). The app will close immediately.`
    : `This will add a firewall rule to drop all traffic to ${conn.remoteIP} (${conn.domain || conn.country || 'this destination'}). You can remove it later via System Settings → Firewall.`
  const btnLabel = isKill ? 'Kill Process' : 'Block IP'
  const btnColor = isKill ? 'var(--accent-red)' : 'var(--accent-amber)'

  return (
    <div onClick={e => e.stopPropagation()} style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(4,6,13,0.75)', backdropFilter: 'blur(8px)',
    }}>
      <div style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-default)',
        borderRadius: 14, padding: '24px 24px 20px',
        maxWidth: 340, width: '90%',
        boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
      }}>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 10, color: 'var(--text-primary)' }}>
          {title}
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.65, marginBottom: 20 }}>
          {body}
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{
            padding: '8px 16px', background: 'none',
            border: '1px solid var(--border-default)', borderRadius: 8,
            color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer',
            fontFamily: 'var(--font-body)',
          }}>Cancel</button>
          <button onClick={onConfirm} style={{
            padding: '8px 16px',
            background: isKill ? 'rgba(239,68,68,0.12)' : 'rgba(245,166,35,0.12)',
            border: `1px solid ${isKill ? 'rgba(239,68,68,0.35)' : 'rgba(245,166,35,0.35)'}`,
            borderRadius: 8, color: btnColor, fontSize: 13,
            fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)',
          }}>{btnLabel}</button>
        </div>
      </div>
    </div>
  )
}

// ─── Kill / Block action bar ──────────────────────────────────────────────────
export default function ActionBar({ conn, allowKill = true, allowBlock = true }) {
  const [confirm, setConfirm]     = useState(null)  // null | 'kill' | 'block'
  const [loading, setLoading]     = useState(false)
  const [result,  setResult]      = useState(null)

  const canKill = allowKill && conn.pid !== undefined && conn.pid !== null && conn.pid !== ''
  const canBlock = allowBlock && Boolean(conn.remoteIP)

  const handleAction = useCallback(async (action) => {
    setLoading(true)
    setResult(null)
    try {
      if (action === 'kill' && !canKill) {
        setResult({ ok: false, error: 'No process ID is available for this alert.' })
        setLoading(false)
        return
      }
      if (action === 'block' && !canBlock) {
        setResult({ ok: false, error: 'No remote IP is available for this alert.' })
        setLoading(false)
        return
      }

      const api = window.electronAPI
      if (!api) {
        // Browser demo — simulate result
        await new Promise(r => setTimeout(r, 800))
        setResult({ ok: true, message: action === 'kill'
          ? `${conn.appName} terminated. (Demo mode — no real process was killed.)`
          : `${conn.remoteIP} blocked. (Demo mode — no real firewall rule was added.)` })
        setLoading(false)
        return
      }
      const res = action === 'kill'
        ? await api.killConnection({ pid: conn.pid, appName: conn.appName })
        : await api.blockIP({ remoteIP: conn.remoteIP, appName: conn.appName })
      if (res.ok) {
        if (action === 'kill') playKillSound()
        else playBlockSound()
      }
      setResult(res)
    } catch (e) {
      setResult({ ok: false, error: 'Something went wrong. Try again.' })
    }
    setLoading(false)
  }, [canBlock, canKill, conn])

  if (!canKill && !canBlock) return null

  return (
    <>
      {confirm && (
        <ConfirmModal
          action={confirm}
          conn={conn}
          onConfirm={() => { setConfirm(null); handleAction(confirm) }}
          onCancel={() => setConfirm(null)}
        />
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
        {canKill && (
          <button
            disabled={loading}
            onClick={e => { e.stopPropagation(); setConfirm('kill') }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '7px 13px',
              background: 'rgba(239,68,68,0.07)',
              border: '1px solid rgba(239,68,68,0.25)',
              borderRadius: 8, color: 'var(--accent-red)',
              fontSize: 12, fontWeight: 500, cursor: loading ? 'default' : 'pointer',
              fontFamily: 'var(--font-body)', opacity: loading ? 0.5 : 1,
              transition: 'background 0.15s, border-color 0.15s',
            }}
            onMouseEnter={e => { if (!loading) { e.currentTarget.style.background='rgba(239,68,68,0.15)'; e.currentTarget.style.borderColor='rgba(239,68,68,0.45)' }}}
            onMouseLeave={e => { e.currentTarget.style.background='rgba(239,68,68,0.07)'; e.currentTarget.style.borderColor='rgba(239,68,68,0.25)' }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
            </svg>
            Kill Process
          </button>
        )}

        {canBlock && (
          <button
            disabled={loading}
            onClick={e => { e.stopPropagation(); setConfirm('block') }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '7px 13px',
              background: 'rgba(245,166,35,0.07)',
              border: '1px solid rgba(245,166,35,0.25)',
              borderRadius: 8, color: 'var(--accent-amber)',
              fontSize: 12, fontWeight: 500, cursor: loading ? 'default' : 'pointer',
              fontFamily: 'var(--font-body)', opacity: loading ? 0.5 : 1,
              transition: 'background 0.15s, border-color 0.15s',
            }}
            onMouseEnter={e => { if (!loading) { e.currentTarget.style.background='rgba(245,166,35,0.15)'; e.currentTarget.style.borderColor='rgba(245,166,35,0.45)' }}}
            onMouseLeave={e => { e.currentTarget.style.background='rgba(245,166,35,0.07)'; e.currentTarget.style.borderColor='rgba(245,166,35,0.25)' }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            Block IP
          </button>
        )}

        {loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-tertiary)' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
              style={{ animation: 'spin 0.8s linear infinite' }}>
              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
            </svg>
            Working…
          </div>
        )}
      </div>

      <ActionResult result={result} onDismiss={() => setResult(null)} />
    </>
  )
}
